import {
  TextInput as AdminTextInput,
  DatagridConfigurable,
  DateField,
  DateInput,
  DateTimeInput,
  FilterButton,
  FilterForm,
  FunctionField,
  Link,
  List as RAList,
  SelectColumnsButton,
  SelectInput,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useUnselectAll,
  useUpdateMany,
} from "react-admin";
import { Box, Button, Card, Chip } from "@mui/material";
import i18next, { languageCode } from "src/locales";

import CustomPagination from "../../CustomPagination";
import { DeliveryNoteDocuments } from "./documents";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { GlobalListStyles } from "src/libs/globalListStyles";
import { IntegrationType } from "src/@generated/schemas";
import cookie from "js-cookie";
import dayjs from "dayjs";
import { url } from "src";

export const QuickFilter = ({
  label,
}: {
  label: string;
  source?: string;
  defaultValue?: any;
}) => {
  return <Chip sx={{ marginBottom: 1 }} label={label} />;
};

export const DeliveryNotesFilter = [
  <AdminTextInput key="id" source="deliveryNoteNumber" size="small" alwaysOn />,
  <AdminTextInput
    key="order.referenceOrderNumber"
    source="order.referenceOrderNumber"
  />,
  <AdminTextInput key="note" source="note" />,
  <AdminTextInput key="order.noteForInvoice" source="order.noteForInvoice" />,
  <AdminTextInput key="order.unloadingCity" source="order.unloadingCity" />,
  <AdminTextInput key="order.unloadingCountry" source="order.unloadingCountry" />,
  <AdminTextInput key="order.orderNumber" source="order.orderNumber" />,
  <DateInput
    key="loadingDateFrom"
    source="order.loadingDate_gte"
    lang={languageCode}
    alwaysOn
  />,
  <DateInput
    key="loadingDateTo"
    source="order.loadingDate_lte"
    lang={languageCode}
    alwaysOn
  />,
  <DateInput
    key="unloadingDateFrom"
    source="order.unloadingDate_gte"
    lang={languageCode}
  />,
  <DateInput
    key="unloadingDateTo"
    source="order.unloadingDate_lte"
    lang={languageCode}
  />,
  <SelectInput
    source="order.isMain"
    alwaysOn
    emptyText={i18next.translate("resources.Global.all")}
    defaultValue={null}
    choices={[
      {
        id: true,
        name: i18next.translate("resources.DeliveryNote.fields.order.justMain"),
      },
      {
        id: false,
        name: i18next.translate(
          "resources.DeliveryNote.fields.order.withoutMain",
        ),
      },
    ]}
  />,
  <AdminTextInput key="order.unloadingName" source="order.unloadingName" />,
  <SelectInput
    key="confirmedAt"
    source="confirmedAt"
    label={
      i18next.translate("resources.DeliveryNote.fields.confirmed") as string
    }
    alwaysOn
    style={{ width: 200 }}
    emptyText={i18next.translate("resources.Global.all")}
    defaultValue={null}
    choices={[
      {
        id: "CONFIRMED",
        name: i18next.translate("resources.DeliveryNote.fields.confirmed"),
      },
      {
        id: "NOT_CONFIRMED",
        name: i18next.translate("resources.DeliveryNote.fields.notConfirmed"),
      },
    ]}
  />,
];

const Actions: React.FC = () => {
  const { identity } = useGetIdentity();

  const filters = identity?.isAdmin
    ? [
        ...DeliveryNotesFilter,
        // <SelectInput
        //   source="order.integrationType"
        //   emptyText={i18next.translate("resources.Global.all")}
        //   choices={[
        //     {
        //       id: "CARLO",
        //       name: "CARLO",
        //     },
        //     {
        //       id: "INGRES",
        //       name: "INGRES",
        //     },
        //   ]}
        // />,
      ]
    : DeliveryNotesFilter;

  return (
    <Box width="100%">
      <TopToolbar>
        <FilterForm filters={filters} />
        <FilterButton
          filters={filters}
          style={{ marginLeft: 30, marginRight: 30 }}
        />
        <SelectColumnsButton />
      </TopToolbar>
    </Box>
  );
};

const BulkActionButtons = () => {
  const [update] = useUpdateMany();
  const dataProvider = useDataProvider();
  const unselectAll = useUnselectAll("DeliveryNote");
  const { selectedIds, data, refetch } = useListContext();

  const handleUpdateMany = async (confirmed = true) => {
    await update("DeliveryNote", {
      ids: selectedIds,
      data: {
        confirmedAt: confirmed ? new Date() : { set: null },
      },
    });

    unselectAll();
    setTimeout(() => refetch(), 400);
  };

  return (
    <>
      <Link
        href="_blank"
        to={`${url}/order/delivery-notes/download-multiple?ids=${selectedIds.join(
          ",",
        )}&access_token=${cookie.get("access_token")}`}
        download
      >
        <Button>Stáhnout dodací listy</Button>
      </Link>

      <Button onClick={() => handleUpdateMany(true)}>Nastavit vyřízené</Button>
      <Button onClick={() => handleUpdateMany(false)}>
        Nastavit nevyřízené
      </Button>
    </>
  );
};

export const DeliveryNotesList = () => {
  return (
    <RAList
      actions={<Actions />}
      sort={{ field: "createdAt", order: "DESC" }}
      pagination={<CustomPagination />}
      filterDefaultValues={{
        order: {
          isMain: true,
        },
      }}
      sx={{
        width: "calc(100vw - 50px)",
      }}
    >
      <GlobalListStyles />
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 180px)",
          overflow: "hidden",
          width: "calc(100vw - 50px)",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DatagridConfigurable
            size="small"
            bulkActionButtons={<BulkActionButtons />}
            rowStyle={(record, index) => ({
              backgroundColor: record.disposedLinks
                ? "#359C35"
                : index % 2 === 0
                ? "#f0f0f0"
                : "#fff",
            })}
            sx={{
              minWidth: "900px",
              "& tr td": { paddingTop: 0, paddingBottom: 0 },
              "& .RaDatagrid-rowOdd": { backgroundColor: "#f0f0f0" },
              "& thead": {
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 2,
              },
            }}
          >
            <TextField source="deliveryNoteNumber" />
            <TextField source="order.referenceOrderNumber" />
            <FunctionField source="sscc" render={(row) => row.sscc.length} />
            <TextField source="note" />
            <TextField source="order.noteForInvoice" />
            <FunctionField
              source="order.externalSystemOrderNumberCarlo"
              label="resources.Order.fields.orderNumber"
              render={(row) =>
                row.order.externalSystemOrderNumberIngres
                  ? `${row.order.orderNumber || "-"}${row.order.externalSystemOrderNumberCarlo
                    ? ` (${row.order.externalSystemOrderNumberCarlo})`
                    : ""
                  }`
                  : row.order.externalSystemOrderNumberCarlo || ""
              }
            />
            {/* <TextField source="order.orderNumber" /> */}
            <DateField source="order.loadingDate" />
            <TextField source="order.loadingName" />
            <TextField source="order.unloadingCity" />
            <TextField source="order.unloadingCountry" />
            <TextField source="order.loadingStreet" />
            <DateField source="order.unloadingDate" />
            <TextField source="order.unloadingStreet" />
            <TextField source="order.unloadingName" />
            <TextField source="locationId" />
            <FunctionField
              source="order.mass"
              render={(row) => `${row.order.mass}kg`}
            />
            <FunctionField
              source="cash"
              render={(row) => `${row.cash || 0},-`}
            />
            <TextField source="goods" sortable={false} />
            <TextField source="numberOfHalfPallets" sortable={false} />
            <TextField
              source="numberOfPackages"
              label="resources.DeliveryNote.fields.numberOfPackages_boxes"
              sortable={false}
            />
            <FunctionField
              source="links"
              sortable={false}
              render={(row) => {
                return row.links?.map((link) => (
                  <div key={link.id}>
                    <a
                      href={`${url}/delivery-note/${
                        link.id
                      }/download?deliveryNoteId=${row.id}&orderId=${
                        row.order.id
                      }&access_token=${cookie.get("access_token")}`}
                      target="_blank"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {i18next.translate("resources.Global.download")}{" "}
                      <FileCopyIcon style={{ fontSize: 11 }} />{" "}
                      {/* {link.createdAt && dayjs(link.createdAt).format('DD.MM. YYYY HH:mm')} */}
                    </a>
                  </div>
                ));
              }}
            />
            <FunctionField
              source="documents"
              sortable={false}
              render={(row) => <DeliveryNoteDocuments row={row} />}
            />
            <FunctionField
              source="confimedAt"
              textAlign="center"
              render={(row) => {
                return row.confirmedAt ? "✓" : "";
              }}
            />
            <FunctionField
              source="createdAt"
              render={(row) => (
                <div style={{ whiteSpace: "nowrap" }}>
                  {dayjs(row.order.createdAt).format("DD.MM YYYY HH:mm")}
                </div>
              )}
            />
          </DatagridConfigurable>
        </Box>
      </Card>
    </RAList>
  );
};
